export enum PlanEntitlement {
    KEY_LIMIT = 'keyLimit',
    PROJECT_LIMIT = 'projectLimit',
    CONTRIBUTOR_LIMIT = 'contributorLimit',
    GLOSSARY_LIMIT = 'glossaryLimit',
    MAU_LIMIT = 'mauLimit',
    AUTOMATION_LIMIT = 'automationLimit',
    AUTOMATION_LIMIT_MT_CHARS = 'automationLimitMtChars',
    MAX_NUMBER_OF_PROJECT_LEVEL_WEBHOOK_HANDLERS = 'maxNumberOfProjectLevelWebhookHandlers',
    MAX_NUMBER_OF_TEAM_WEBHOOK_LEVEL_HANDLERS = 'maxNumberOfTeamWebhookLevelHandlers',
    OTA_TRAFFIC_LIMIT = 'otaTrafficLimit',
    AI_USAGE_WORDS_LIMIT = 'aiUsageWordsLimit',
    WORKFLOWS_NUMBER_LIMIT = 'workflowsNumberLimit',
    WORKFLOWS_TEMPLATE_GATE_LEVEL = 'workflowsTemplateGateLevel',
    PROCESSED_WORDS_LIMIT = 'processedWordsLimit',
    HOSTED_WORDS_LIMIT = 'hostedWordsLimit',
    ADVANCED_SEATS_LIMIT = 'advancedSeatsLimit',
    PRO_AI_TRANSLATIONS_LIMIT = 'proAiTranslationsLimit',
    LANGUAGES_LIMIT = 'languagesLimit',
    API_CALLS_RATE_LIMIT = 'apiCallsRateLimit',
    INTEGRATIONS_LIMIT = 'integrationsLimit',
    AUTOMATION_V2_LIMIT = 'automationV2Limit',
}

export enum FeatureCode {
    LIVE_EDIT = 'live_edit',
    LIVE_JS = 'live_js',
    MOBILE_INSIGHTS = 'mobile_insights',
    API_CLI = 'api_cli',
    PROJECT_AUTOMATIONS = 'project_automations',
    PROJECT_ACTIVITY = 'project_activity',
    TRANSLATOR_STATS = 'translator_stats',
    AUTOMATIC_SNAPSHOTS = 'automatic_snapshots',
    UPLOAD_SNAPSHOTS = 'upload_snapshots',
    BULK_ACTIONS_SNAPSHOTS = 'bulk_actions_snapshots',
    SHARED_GLOSSARIES = 'shared_glossaries',
    USER_GROUPS = 'user_groups',
    CUSTOM_TRANSLATION_STATUSES = 'custom_translation_statuses',
    INTEGRATION_GITHUB = 'integration_github',
    INTEGRATION_GITHUB_ENTERPRISE = 'integration_github_enterprise',
    INTEGRATION_AZURE = 'integration_azure',
    INTEGRATION_ASANA = 'integration_asana',
    INTEGRATION_ZENDESK = 'integration_zendesk',
    INTEGRATION_ZENDESK_GUIDE = 'integration_zendesk_guide',
    INTEGRATION_ZENDESK_DC = 'integration_zendesk_dc',
    INTEGRATION_INTERCOM_ARTICLES = 'integration_intercom_articles',
    INTEGRATION_S3 = 'integration_s3',
    INTEGRATION_GCS = 'integration_gcs',
    INTEGRATION_GITLAB = 'integration_gitlab',
    INTEGRATION_BITBUCKET = 'integration_bitbucket',
    INTEGRATION_BITBUCKET_ENTERPRISE = 'integration_bitbucket_enterprise',
    INTEGRATION_CONTENTFUL = 'integration_contentful',
    INTEGRATION_WORDPRESS = 'integration_wordpress',
    INTEGRATION_SALESFORCE_CRM = 'integration_salesforce_crm',
    INTEGRATION_SALESFORCE_KNOWLEDGE_ARTICLES = 'integration_salesforce_knowledge_articles',
    INTEGRATION_JIRA_CLOUD = 'integration_jira_cloud',
    INTEGRATION_JIRA_SERVER = 'integration_jira_server',
    INTEGRATION_HUBSPOT = 'integration_hubspot',
    INTEGRATION_STORYBLOK = 'integration_storyblok',
    INLINE_DEEPL = 'inline_deepl',
    CHAINED_TASKS = 'chained_tasks',
    CREATE_TASKS_FLOW_V2 = 'create_tasks_flow_v2',
    KEY_TYPE_IMAGE = 'key_type_image',
    OFFLINE_TRANSLATION = 'offline_translation',
    TASK_TRANSLATION_LOCKS = 'task_translation_locks',
    ADVANCED_SECURITY = 'advanced_security',
    ADVANCED_SECURITY_SSO = 'advanced_security_sso',
    ADVANCED_SECURITY_SECURE_PASSWORD = 'advanced_security_secure_password',
    TM_MANAGEMENT = 'tm_management',
    TRANSLATION_MEMORY = 'translation_memory',
    ALL_TRANSLATION_PROVIDERS = 'all_translation_providers',
    PAGED_DOCUMENTS = 'paged_documents',
    AUDIT_LOGS = 'audit_logs',
    BRANCHING = 'branching',
    VENDOR_RATE_PROFILES = 'vendor_rate_profiles',
    SCREENSHOTS = 'screenshots',
    TRANSLATION_HISTORY = 'translation_history',
    MACHINE_TRANSLATIONS = 'machine_translations',
    GLOSSARY = 'glossary',
    PLUGIN_FIGMA = 'plugin_figma',
    PLUGIN_ADOBE_XD = 'plugin_adobe_xd',
    PLUGIN_SKETCH = 'plugin_sketch',
    SEGMENTATION = 'segmentation',
    CUSTOM_PLACEHOLDER = 'custom_placeholder',
    INTEGRATION_CONTENTSTACK = 'integration_contentstack',
    INTEGRATION_FRESHDESK = 'integration_freshdesk',
    INTEGRATION_TYPEFORM = 'integration_typeform',
    INTEGRATION_ITERABLE = 'integration_iterable',
    INTEGRATION_YOUTUBE = 'integration_youtube',
    INTEGRATION_SMC = 'integration_smc',
    INTEGRATION_MARKETO = 'integration_marketo',
    INTEGRATION_GOOGLE_PLAY = 'integration_google_play',
    INTEGRATION_DITTO = 'integration_ditto',
    INTEGRATION_BRAZE = 'integration_braze',
    INTEGRATION_CONTENTFUL_NATIVE = 'integration_contentful_native',
    INTEGRATION_WEBFLOW = 'integration_webflow',
    INTEGRATION_CLEVERTAP = 'integration_clevertap',
    BBCODE = 'bbcode',
    CUSTOM_PROCESSOR = 'custom_processor',
    OTA = 'ota',
    REVIEW_CENTER = 'review_center',
    AI = 'ai',
    DOCUMENT_TYPE_PROJECT_V2 = 'document_type_project_v2',
    UNDO_BULK_ACTION = 'undo_bulk_action',
    ANALYTICS_FILTERS = 'analytics_filters',
    WORKFLOWS_WEEKLY_TRIGGER = 'workflows_weekly_trigger',
    WORKFLOWS_CUSTOMIZATION = 'workflows_customization',
    STYLE_GUIDE = 'style_guide',
}

export enum MetricsStatus {
    /**
     * Usage is comfortably within the allowed limit.
     * currentValue: 15000 < limitValue: 100000
     */
    OK = 'OK',

    /**
     * Usage is approaching the allowed limit.
     * currentValue: 95678 >= 80% of limitValue: 100000
     * @note The threshold can be a generic hardcoded value on BE, we can assume 80% is a good start.
     */
    NEAR_LIMIT = 'NEAR_LIMIT',

    /**
     * Usage has reached the exact limit allowed by the plan.
     * @example currentValue: 5 == limitValue: 5
     */
    LIMIT_REACHED = 'LIMIT_REACHED',

    /**
     * Usage has surpassed the allowed limit.
     * @example currentValue: 3 > limitValue: 2
     */
    EXCEEDED = 'EXCEEDED',

    /**
     * This metric does not have a usage limit under the current plan.
     * @example currentValue: 157890, limitValue: null or 999999999
     */
    UNLIMITED = 'UNLIMITED',

    /**
     * The feature or metric is not included in the team's current plan.
     * @example currentValue: 0, limitValue: 0
     */
    UPGRADE_REQUIRED = 'UPGRADE_REQUIRED',

    /**
     * The system could not determine the current usage or status for this metric. (Equivalent to a partial API HTTP 500)
     * @example currentValue: null, limitValue: 1000 (if known)
     */
    UNKNOWN = 'UNKNOWN',
}
